import { clsx, type ClassValue } from "clsx"
import { twMerge } from "tailwind-merge"
import {
  getSessionStorageItem,
  setSessionStorageItem,
} from "./browserStorage"

export function cn(...inputs: ClassValue[]) {
  return twMerge(clsx(inputs))
}

export const ATTRIBUTION_QUERY_KEYS = [
  "utm_id",
  "utm_source",
  "utm_medium",
  "utm_campaign",
  "utm_term",
  "utm_content",
  "utm_source_platform",
  "utm_creative_format",
  "utm_marketing_tactic",
  "gclid",
  "gbraid",
  "wbraid",
  "gclsrc",
  "dclid",
  "gad_source",
  "gad_campaignid",
  "fbclid",
  "msclkid",
  "li_fat_id",
  "ttclid",
  "twclid",
  "epik",
  "rdt_cid",
  "ScCid",
  "sccid",
  "mc_cid",
  "mc_eid",
  "_kx",
  "mkt_tok",
  "irclickid",
  "srsltid",
  "hsa_acc",
  "hsa_cam",
  "hsa_grp",
  "hsa_ad",
  "hsa_src",
  "hsa_tgt",
  "hsa_kw",
  "hsa_mt",
  "hsa_net",
  "hsa_ver",
] as const

export const HUBSPOT_MEETING_URL =
  "https://meetings.hubspot.com/isantini/mvo-contact-us"
export const HUBSPOT_MEETING_EMBED_URL = `${HUBSPOT_MEETING_URL}?embed=true`
export const HUBSPOT_PARTNER_MEETING_EMBED_URL =
  "https://meetings.hubspot.com/isantini/partners?embed=true"

const HUBSPOT_ATTRIBUTION_HOSTS = new Set([
  "share.hsforms.com",
  "meetings.hubspot.com",
])

const PERSISTED_ATTRIBUTION_STORAGE_KEY = "persistedUTM"

export function getCostSegEZAddress(currentPath: string, path?: string): string {
  return `https://costsegez.com${path ?? ''}?utm_medium=referral&utm_source=${encodeURIComponent(DOMAIN_NAME + currentPath)}`
}


export function slugify(input: string) {
  return (
    input
      .toLowerCase()
      .trim()
      // remove invalid chars
      .replace(/[^a-z0-9\s-]/g, "")
      // collapse whitespace and replace by -
      .replace(/\s+/g, "-")
      // collapse multiple -
      .replace(/-+/g, "-")
  )
}

export const DOMAIN_NAME = "https://mvocostseg.com"

export const PAGE_PATH = {
  ESTIMATE: '/estimate/',
  SERVICES: '/our-services/',
  HOW_WORKS: '/how-cost-seg-works/',
  RENTALS: '/cost-seg-and-short-term-rentals/',
  ABOUT: '/about'
};

export const getAttributionQueryFromSearch = (search: string) => {
  const params = new URLSearchParams(search)
  const attributionParams = new URLSearchParams()

  ATTRIBUTION_QUERY_KEYS.forEach((key) => {
    const value = params.get(key)
    if (value) attributionParams.set(key, value)
  })

  const query = attributionParams.toString()
  return query ? `?${query}` : ""
}

export const persistAttributionParams = (search: string) => {
  const attributionQuery = getAttributionQueryFromSearch(search)
  if (!attributionQuery) return
  if (getPersistedAttributionQuery() === attributionQuery) return

  setSessionStorageItem(PERSISTED_ATTRIBUTION_STORAGE_KEY, attributionQuery)
}

export const persistAttributionFromLocation = (location: { search: string }) => {
  persistAttributionParams(location.search)
}

export const getPersistedAttributionQuery = () => {
  return getSessionStorageItem(PERSISTED_ATTRIBUTION_STORAGE_KEY) || ""
}

const getHubSpotAttributionDestination = (url: string) => {
  if (typeof window === "undefined") return null

  try {
    const destination = new URL(url, window.location.origin)
    return HUBSPOT_ATTRIBUTION_HOSTS.has(destination.hostname)
      ? destination
      : null
  } catch {
    return null
  }
}

export const withHubSpotAttribution = (url: string) => {
  const destination = getHubSpotAttributionDestination(url)
  if (!destination) return url

  const attributionQuery = getPersistedAttributionQuery()
  if (!attributionQuery) return url

  try {
    const attributionParams = new URLSearchParams(attributionQuery)
    attributionParams.forEach((value, key) => {
      if (!destination.searchParams.has(key)) {
        destination.searchParams.set(key, value)
      }
    })

    return destination.toString()
  } catch {
    return url
  }
}

export const withUTM = (path: string) => {
  const attributionQuery = getPersistedAttributionQuery();
  if (!attributionQuery) return path;

  const hashIndex = path.indexOf("#");
  const pathWithoutHash = hashIndex === -1 ? path : path.slice(0, hashIndex);
  const hash = hashIndex === -1 ? "" : path.slice(hashIndex);
  const queryIndex = pathWithoutHash.indexOf("?");

  if (queryIndex === -1) {
    return `${pathWithoutHash}${attributionQuery}${hash}`;
  }

  const existingQuery = pathWithoutHash.slice(queryIndex + 1);
  const existingParams = new URLSearchParams(existingQuery);
  const missingAttributionParams = new URLSearchParams();

  new URLSearchParams(attributionQuery).forEach((value, key) => {
    if (!existingParams.has(key)) {
      missingAttributionParams.set(key, value);
    }
  });

  const missingQuery = missingAttributionParams.toString();
  if (!missingQuery) return path;

  const separator = existingQuery ? "&" : "";
  return `${pathWithoutHash}${separator}${missingQuery}${hash}`;
};
